import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLeaseCalculator } from '../../hooks/useLeaseCalculator';
import { useLanguage } from '../../i18n/LanguageContext';
import { LanguageSelector } from './LanguageSelector';
import { Toaster } from '../ui/sonner';

export function EmbedCalculator() {
    const [searchParams] = useSearchParams();
    const { inputs, updateInput, results } = useLeaseCalculator();
    const { t, formatCurrency, language, setLanguage, currencySymbol, currencyConfig } = useLanguage();

    // Allow ?lang= param from embed URL to override language
    useEffect(() => {
        const langParam = searchParams.get('lang');
        if (langParam && langParam !== language) {
            setLanguage(langParam);
        }
    }, [searchParams, setLanguage, language]);

    const convertPrice = (eurAmount) => (eurAmount * currencyConfig.rate).toFixed(2);
    
    const config = {
        accentColor: searchParams.get('accent') || '#4CAF50',
        buttonColor: searchParams.get('buttonColor') || '#1a1a1a',
        terminalName: searchParams.get('terminal') || 'Adyen S1U2 Terminal',
        koopPrijs: searchParams.get('koopPrijs') || '699',
        muurbevestiging: searchParams.get('muurPrijs') || '59',
        images: (searchParams.get('images') || `${process.env.REACT_APP_TERMINAL_IMG_1 || ''},${process.env.REACT_APP_TERMINAL_IMG_2 || ''},${process.env.REACT_APP_TERMINAL_IMG_3 || ''}`).split(',').filter(Boolean),
        logo: searchParams.get('logo') || process.env.REACT_APP_LOGO_URL || '/images/logo.jpeg',
        showKoop: searchParams.get('showKoop') !== 'false',
        showLease: searchParams.get('showLease') !== 'false',
    };

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [selectedChoice, setSelectedChoice] = useState(null);
    const [formData, setFormData] = useState({
        bedrijfsnaam: '',
        email: '',
        telefoon: '',
        interesse: 'lease'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    useEffect(() => {
        if (config.images.length <= 1) return;
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % config.images.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [config.images.length]);

    const scrollToSection = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                aantal_douches: inputs.aantalDouches,
                douchebeurten_per_dag: inputs.douchebeurtenWaarde,
                prijs_per_minuut: inputs.prijsPerMinuut,
                lease_prijs_per_terminal: inputs.leasePrijsPerTerminal,
                maanden_open: inputs.maandenOpen,
                bruto_omzet: results.brutoOmzet,
                totale_kosten: results.totaleKosten,
                netto_winst_maand: results.nettoWinstMaand,
                netto_winst_jaar: results.nettoWinstJaar,
                breakeven_prijs_per_minuut: results.breakevenPrijsPerMinuut
            };
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            setIsSubmitted(true);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const cssVars = {
        '--accent-color': config.accentColor,
        '--button-color': config.buttonColor,
    };

    return (
        <div style={cssVars} className="embed-calculator font-['Manrope'] bg-white">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&display=swap');
                .embed-calculator .accent-bg { background-color: var(--accent-color); }
                .embed-calculator .accent-text { color: var(--accent-color); }
                .embed-calculator .accent-border { border-color: var(--accent-color); }
                .embed-calculator .accent-bg-light { background-color: color-mix(in srgb, var(--accent-color) 10%, transparent); }
                .embed-calculator .button-bg { background-color: var(--button-color); }
                .embed-calculator .button-bg:hover { background-color: color-mix(in srgb, var(--button-color) 90%, white); }
            `}</style>

            {/* Language selector bar */}
            <div className="flex justify-end px-4 py-2 bg-gray-50 border-b border-gray-100">
                <LanguageSelector />
            </div>

            {/* Hero Section */}
            <section className="py-12 px-4 bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                        <div className="text-center lg:text-left">
                            <span className="inline-block px-4 py-1.5 accent-bg-light accent-text text-sm font-semibold rounded-full mb-4">
                                {config.terminalName}
                            </span>
                            <h1 className="text-3xl lg:text-5xl font-bold text-gray-900 font-['Outfit'] mb-4">
                                {t('hero_title_1')}<br />
                                <span className="accent-text">{t('hero_title_2')}</span>
                            </h1>
                            <p className="text-gray-600 mb-6">{t('hero_desc')}</p>
                            
                            <div className="mb-6">
                                <h2 className="text-lg font-bold text-gray-900 mb-3">{t('hero_choice')}</h2>
                                <div className="flex gap-3 justify-center lg:justify-start">
                                    {config.showKoop && (
                                        <button 
                                            onClick={() => { setSelectedChoice('koop'); scrollToSection('embed-koop'); }}
                                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition-all ${
                                                selectedChoice === 'koop' ? 'button-bg text-white' : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-gray-300'
                                            }`}
                                            data-testid="embed-choice-koop"
                                        >
                                            {t('hero_buy')}
                                        </button>
                                    )}
                                    {config.showLease && (
                                        <button 
                                            onClick={() => { setSelectedChoice('lease'); scrollToSection('embed-lease'); }}
                                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition-all ${
                                                selectedChoice === 'lease' ? 'accent-bg text-white' : 'bg-white border-2 border-gray-200 text-gray-700 hover:accent-border'
                                            }`}
                                            data-testid="embed-choice-lease"
                                        >
                                            {t('hero_lease')}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4 max-w-xs mx-auto lg:mx-0">
                                <div><div className="text-lg font-bold text-gray-900">IP65</div><div className="text-xs text-gray-500">{t('hero_waterproof')}</div></div>
                                <div><div className="text-lg font-bold text-gray-900">4G</div><div className="text-xs text-gray-500">{t('hero_connectivity')}</div></div>
                                <div><div className="text-lg font-bold text-gray-900">PCI</div><div className="text-xs text-gray-500">{t('hero_certified')}</div></div>
                            </div>
                        </div>

                        {config.images.length > 0 && (
                            <div className="relative">
                                <div className="rounded-xl overflow-hidden shadow-lg">
                                    {config.images.map((img, idx) => (
                                        <img
                                            key={idx}
                                            src={img}
                                            alt={`Terminal ${idx + 1}`}
                                            className={`w-full h-auto transition-opacity duration-700 ${idx === currentImageIndex ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
                                        />
                                    ))}
                                </div>
                                {config.images.length > 1 && (
                                    <div className="flex justify-center gap-2 mt-3">
                                        {config.images.map((_, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => setCurrentImageIndex(idx)}
                                                className={`h-2 rounded-full transition-all ${idx === currentImageIndex ? 'w-6 accent-bg' : 'w-2 bg-gray-300'}`}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Koop Section */}
            {config.showKoop && (
                <section id="embed-koop" className="py-12 px-4 bg-white">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-8">
                            <span className="inline-block px-4 py-1.5 bg-gray-900 text-white text-sm font-semibold rounded-full mb-3">{t('koop_badge')}</span>
                            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 font-['Outfit']">{config.terminalName}</h2>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                                <div className="text-sm text-gray-500 uppercase mb-1">{t('koop_price_label')}</div>
                                <div className="text-4xl font-bold text-gray-900 font-['Outfit'] mb-4">
                                    {formatCurrency(parseFloat(config.koopPrijs))} <span className="text-base font-normal text-gray-500">{t('koop_excl_vat')}</span>
                                </div>
                                
                                <div className="border-t border-gray-200 pt-4 mb-4">
                                    <div className="text-sm font-semibold text-gray-700 uppercase mb-3">{t('koop_included')}</div>
                                    <ul className="space-y-2 text-gray-700 text-sm">
                                        {['koop_item_terminal', 'koop_item_relay_door', 'koop_item_relay_shower', 'koop_item_screen', 'koop_item_schema', 'koop_item_manual'].map(key => (
                                            <li key={key} className="flex items-center gap-2"><span className="accent-text">&#10003;</span> {t(key)}</li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="border-t border-gray-200 pt-4 mb-4">
                                    <div className="text-sm font-semibold text-gray-700 uppercase mb-2">{t('koop_optional')}</div>
                                    <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                                        <span className="font-medium">{t('koop_mount')}</span>
                                        <span className="font-bold">{formatCurrency(parseFloat(config.muurbevestiging))} ex</span>
                                    </div>
                                </div>

                                <button 
                                    onClick={() => scrollToSection('embed-form')}
                                    className="w-full py-3 button-bg text-white font-semibold rounded-lg"
                                    data-testid="embed-koop-cta"
                                >
                                    {t('koop_cta')}
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div className="bg-white rounded-xl p-5 border border-gray-200">
                                    <h3 className="font-bold text-gray-900 mb-3">{t('koop_transaction_title')}</h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                                            <span>{t('koop_under_10').replace('{0}', formatCurrency(10))}</span>
                                            <span className="font-bold">{formatCurrency(0.20)}</span>
                                        </div>
                                        <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                                            <span>{t('koop_over_10').replace('{0}', formatCurrency(10))}</span>
                                            <span className="font-bold">{formatCurrency(0.40)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="accent-bg-light rounded-xl p-5 border accent-border border-opacity-20">
                                    <h3 className="font-bold text-gray-900 mb-3">{t('koop_portal_title')}</h3>
                                    <ul className="space-y-2 text-sm text-gray-700">
                                        <li>&#8226; {t('koop_portal_transactions')}</li>
                                        <li>&#8226; {t('koop_portal_qr')}</li>
                                        <li>&#8226; {t('koop_portal_rates')}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Lease Calculator Section */}
            {config.showLease && (
                <section id="embed-lease" className="py-12 px-4 bg-gray-50">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-8">
                            <span className="inline-block px-4 py-1.5 accent-bg text-white text-sm font-semibold rounded-full mb-3">{t('calc_badge')}</span>
                            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 font-['Outfit']">{t('calc_title')}</h2>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white rounded-xl p-6 border border-gray-100">
                                <div className="space-y-6">
                                    {[
                                        { label: t('slider_readers'), key: 'aantalDouches', min: 1, max: 40, step: 1 },
                                        { label: t(inputs.douchebeurtenPerDag ? 'slider_showers_day' : 'slider_showers_month'), key: 'douchebeurtenWaarde', min: inputs.douchebeurtenPerDag ? 5 : 100, max: inputs.douchebeurtenPerDag ? 100 : 3000, step: inputs.douchebeurtenPerDag ? 5 : 50 },
                                        { label: t('slider_price'), key: 'prijsPerMinuut', min: 0.20, max: 1.50, step: 0.05, isCurrency: true },
                                        { label: t('slider_shower_time'), key: 'gemiddeldeDouchetijd', min: 5, max: 10, step: 1, suffix: ` ${t('slider_min')}` },
                                        { label: t('slider_months_open'), key: 'maandenOpen', min: 1, max: 12, step: 1 },
                                        { label: t('slider_occupancy'), key: 'bezettingsgraad', min: 40, max: 100, step: 5, suffix: '%' },
                                    ].map((slider) => (
                                        <div key={slider.key} data-testid={`embed-slider-${slider.key}`}>
                                            <div className="flex justify-between mb-2">
                                                <span className="text-sm font-semibold text-gray-700 uppercase">{slider.label}</span>
                                                <span className="font-bold text-gray-900">
                                                    {slider.isCurrency 
                                                        ? `${currencySymbol}${convertPrice(inputs[slider.key])}` 
                                                        : `${inputs[slider.key]}${slider.suffix || ''}`}
                                                </span>
                                            </div>
                                            <input
                                                type="range"
                                                min={slider.min}
                                                max={slider.max}
                                                step={slider.step}
                                                value={inputs[slider.key] ?? slider.min}
                                                onChange={(e) => updateInput(slider.key, parseFloat(e.target.value))}
                                                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                                                style={{
                                                    background: `linear-gradient(to right, var(--accent-color) 0%, var(--accent-color) ${(((inputs[slider.key] ?? slider.min) - slider.min) / (slider.max - slider.min)) * 100}%, #e5e7eb ${(((inputs[slider.key] ?? slider.min) - slider.min) / (slider.max - slider.min)) * 100}%, #e5e7eb 100%)`
                                                }}
                                            />
                                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                                <span>{slider.isCurrency ? `${currencySymbol}${convertPrice(slider.min)}` : `${slider.min}${slider.suffix || ''}`}</span>
                                                <span>{slider.isCurrency ? `${currencySymbol}${convertPrice(slider.max)}` : `${slider.max}${slider.suffix || ''}`}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Contract info */}
                                <div className="mt-6 p-3 bg-gray-50 rounded-lg border border-gray-100 text-xs text-gray-600">
                                    <span className="font-semibold text-gray-900">{t('lease_fixed_price').replace('{0}', formatCurrency(39))}</span>
                                    {' '}&bull;{' '}{t('lease_contract_info')}
                                    {' '}&bull;{' '}{t('lease_early_exit')}
                                </div>
                            </div>

                            {/* Results */}
                            <div className="bg-white rounded-xl p-6 border border-gray-100" data-testid="embed-results">
                                <h3 className="font-bold text-gray-900 mb-4">{t('results_title')}</h3>
                                <div className="space-y-3">
                                    {/* Correctie */}
                                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-blue-700">{t('results_correction_title')} ({results.bezettingsgraad}%)</span>
                                            <span className="font-bold text-blue-800">{results.douchebeurtenPerMaand} {t('results_showers_month')}</span>
                                        </div>
                                        <div className="text-xs text-blue-500 mt-1">{t('results_correction_factors')}</div>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg flex justify-between">
                                        <span className="text-gray-600">{t('results_gross_revenue')}</span>
                                        <span className="font-bold">{formatCurrency(results.brutoOmzet)}</span>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg flex justify-between">
                                        <span className="text-gray-500 text-sm">{t('results_transaction_costs')}</span>
                                        <span className="font-medium text-gray-500">- {formatCurrency(results.transactiekosten)}</span>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg flex justify-between">
                                        <span className="text-gray-600">{t('results_net_revenue')}</span>
                                        <span className="font-bold">{formatCurrency(results.nettoOmzet)}</span>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <div className="flex justify-between mb-1">
                                            <span className="text-gray-600">{t('results_monthly_recra')}</span>
                                            <span className="font-bold">{formatCurrency(results.leaseKostenPerMaand)}</span>
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            {formatCurrency(39)} x {results.aantalDouches} {t('results_readers')}
                                        </div>
                                    </div>
                                    {/* MRG if active */}
                                    {results.mrgActief && (
                                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-amber-700">{t('results_mrg_title')}</span>
                                                <span className="font-bold text-amber-800">{formatCurrency(results.mrgBijbetaling)}</span>
                                            </div>
                                            <div className="text-xs text-amber-500 mt-1">
                                                {t('results_mrg_calc').replace('{0}', results.mrgTekort).replace('{1}', formatCurrency(0.10))}
                                            </div>
                                        </div>
                                    )}
                                    <div className="p-3 bg-gray-50 rounded-lg flex justify-between">
                                        <span className="text-gray-500 text-sm">{t('results_own_costs')}</span>
                                        <span className="font-medium text-gray-500">{formatCurrency(results.exploitatieKosten)}</span>
                                    </div>
                                    <div className={`p-3 rounded-lg flex justify-between ${results.nettoWinstMaand >= 0 ? 'accent-bg-light' : 'bg-red-50'}`}>
                                        <span className={results.nettoWinstMaand >= 0 ? 'accent-text' : 'text-red-600'}>{t('results_net_profit_month')}</span>
                                        <span className={`font-bold ${results.nettoWinstMaand >= 0 ? 'accent-text' : 'text-red-600'}`}>{formatCurrency(results.nettoWinstMaand)}</span>
                                    </div>
                                    <div className={`p-3 rounded-lg flex justify-between ${results.nettoWinstJaar >= 0 ? 'accent-bg-light' : 'bg-red-50'}`}>
                                        <span className={results.nettoWinstJaar >= 0 ? 'accent-text' : 'text-red-600'}>{t('results_net_profit_year')}</span>
                                        <span className={`font-bold ${results.nettoWinstJaar >= 0 ? 'accent-text' : 'text-red-600'}`}>{formatCurrency(results.nettoWinstJaar)}</span>
                                    </div>
                                    <div className="p-3 bg-gray-900 text-white rounded-lg flex justify-between">
                                        <span>{t('results_breakeven')}</span>
                                        <span className="font-bold">{formatCurrency(results.breakevenPrijsPerMinuut)}/min</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Contact Form */}
            <section id="embed-form" className="py-12 px-4 bg-white">
                <div className="max-w-lg mx-auto">
                    {isSubmitted ? (
                        <div className="text-center p-8 bg-gray-50 rounded-xl" data-testid="embed-form-success">
                            <div className="w-16 h-16 accent-bg-light rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="accent-text text-2xl">&#10003;</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('form_success_title')}</h3>
                            <p className="text-gray-600">{t('form_success_desc').replace('{0}', formData.interesse === 'koop' ? t('form_success_buy') : t('form_success_lease'))}</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-lg" data-testid="embed-form">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('form_title')}</h3>
                            <p className="text-gray-600 mb-4 text-sm">{t('form_subtitle')}</p>
                            
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setFormData(p => ({ ...p, interesse: 'koop' }))}
                                        className={`p-3 rounded-lg border-2 font-semibold transition-all ${formData.interesse === 'koop' ? 'button-bg text-white border-transparent' : 'border-gray-200 text-gray-700'}`}
                                        data-testid="embed-interest-koop"
                                    >
                                        {t('form_buy')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(p => ({ ...p, interesse: 'lease' }))}
                                        className={`p-3 rounded-lg border-2 font-semibold transition-all ${formData.interesse === 'lease' ? 'accent-bg text-white border-transparent' : 'border-gray-200 text-gray-700'}`}
                                        data-testid="embed-interest-lease"
                                    >
                                        {t('form_lease')}
                                    </button>
                                </div>
                                
                                <input
                                    type="text"
                                    placeholder={t('form_company_placeholder')}
                                    value={formData.bedrijfsnaam}
                                    onChange={(e) => setFormData(p => ({ ...p, bedrijfsnaam: e.target.value }))}
                                    required
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                                    data-testid="embed-input-bedrijfsnaam"
                                />
                                <input
                                    type="email"
                                    placeholder={t('form_email_placeholder')}
                                    value={formData.email}
                                    onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                                    required
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                                    data-testid="embed-input-email"
                                />
                                <input
                                    type="tel"
                                    placeholder={t('form_phone_placeholder')}
                                    value={formData.telefoon}
                                    onChange={(e) => setFormData(p => ({ ...p, telefoon: e.target.value }))}
                                    required
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                                    data-testid="embed-input-telefoon"
                                />
                                
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full py-3 button-bg text-white font-semibold rounded-lg disabled:opacity-50"
                                    data-testid="embed-submit-btn"
                                >
                                    {isSubmitting ? t('form_sending') : t('form_submit')}
                                </button>
                            </form>
                            <p className="text-xs text-gray-400 text-center mt-3">{t('form_privacy')}</p>
                        </div>
                    )}
                </div>
            </section>
            
            <Toaster position="top-right" richColors />
        </div>
    );
}
